/**
 * TaskFloatingCommentInput
 *
 * Page-level floating comment input. Lives outside the Comments tab so it
 * stays visible while the user browses History or Campaigns tabs.
 *
 * Collapsed: renders a pill at the bottom-center of the viewport.
 * Expanded: opens the full toolbar + textarea + attachments editor.
 */

import React, { memo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Row, Col, Mentions, Tag, Button, Tooltip, message } from 'antd';
import {
  PaperClipOutlined,
  PictureOutlined,
  CodeOutlined,
  LoadingOutlined,
  BoldOutlined,
  ItalicOutlined,
  StrikethroughOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  MessageOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import { CopyBlock } from 'react-code-blocks';

import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';
import { handleUploadFilesToS3, hasCommentContent } from '../../utils/task.utils';
import FileDropPasteArea from '../../utils/FileDropPasteArea';
import { ENABLED_USER_FILTER } from '../../../../../core/utils/enabledUserFilter';
import { allowedFileExtensions, allowedImageExtensions } from '../../configs/task.config';
import useReopenOnComment from '../../hooks/useReopenOnComment';
import { TaskReopenPromptModal } from '../modals';

const TaskFloatingCommentInput = ({ currentTaskId, onCommentPosted, formRef }) => {
  const [form] = Form.useForm();
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  // Expose the internal form to parents that need to read draft state
  // (e.g. drawer's unsaved-comment guard).
  useEffect(() => {
    if (formRef) {
      formRef.current = form;
    }
  }, [form, formRef]);

  const { currentUser, createComment, refetchTask, fetchMentionUsers, mentionUsers, currentTask } =
    useTaskContext();

  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [inputExpanded, setInputExpanded] = useState(false);

  // DOM <textarea> node — works for both Input.TextArea and Mentions refs.
  const getCommentTextarea = () =>
    (textareaRef.current &&
      (textareaRef.current.textarea ||
        (textareaRef.current.resizableTextArea &&
          textareaRef.current.resizableTextArea.textArea))) ||
    null;

  const [mentionSearch, setMentionSearch] = useState('');
  const mentionOrgId =
    currentUser && currentUser.role && currentUser.role.org && currentUser.role.org.id;

  // Load task-management users in the current org for the @ dropdown.
  useEffect(() => {
    if (!mentionOrgId) return;
    fetchMentionUsers({
      variables: {
        first: 200,
        skip: 0,
        where: {
          AND: [
            {
              roles_some: {
                org: { id: mentionOrgId },
                roleItems_some: { feature: 'TASK_MANAGEMENT' },
              },
            },
            ENABLED_USER_FILTER,
          ],
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentionOrgId]);

  // Map @username mentions back to user IDs, matching the backend's boundary so trailing
  // punctuation (e.g. "@user,") resolves. Longest username first, blanking each match so a
  // shorter prefix (e.g. "@randall" inside "@randall.tan") can't double-count.
  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const getMentionedUserIds = (text) => {
    if (!text) return [];
    let remaining = text;
    const ids = [];
    [...(mentionUsers || [])]
      .filter((u) => u.username)
      .sort((a, b) => b.username.length - a.username.length)
      .forEach((u) => {
        const re = new RegExp(`@${escapeRegExp(u.username)}(?![A-Za-z0-9_])`, 'g');
        const replaced = remaining.replace(re, ' ');
        if (replaced !== remaining) {
          ids.push(u.id);
          remaining = replaced;
        }
      });
    return ids;
  };

  // Options for the @ dropdown, filtered by what the user is typing.
  const renderMentionOptions = () =>
    (mentionUsers || [])
      .filter((u) => {
        if (!u.username) return false;
        if (!mentionSearch) return true;
        const q = mentionSearch.toLowerCase();
        return (
          (u.name && u.name.toLowerCase().includes(q)) || u.username.toLowerCase().includes(q)
        );
      })
      .slice(0, 20)
      .map((u) => (
        <Mentions.Option key={u.id} value={u.username}>
          {u.name ? `${u.name} (@${u.username})` : `@${u.username}`}
        </Mentions.Option>
      ));

  const expandInput = () => {
    setInputExpanded(true);
    setTimeout(() => {
      const textarea = getCommentTextarea();
      textarea?.focus();
    }, 0);
  };

  const collapseInput = () => {
    setInputExpanded(false);
  };

  const handleUploadFileClick = () => fileInputRef.current.click();
  const handleUploadImageClick = () => imageInputRef.current.click();

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const invalidFiles = files.filter(
      (file) =>
        !allowedFileExtensions.some((ext) =>
          file.name.toLowerCase().endsWith(ext.replace('.', '').toLowerCase())
        )
    );
    if (invalidFiles.length > 0) {
      message.error('Images are not allowed in this field.');
      e.target.value = null;
      return;
    }
    form.setFieldsValue({
      attachedFiles: [...(form.getFieldValue('attachedFiles') || []), ...files],
    });
    e.target.value = null;
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const invalidFiles = files.filter(
      (file) =>
        !allowedImageExtensions.some((ext) =>
          file.name.toLowerCase().endsWith(ext.replace('.', '').toLowerCase())
        )
    );
    if (invalidFiles.length > 0) {
      message.error('Only image files are allowed.');
      e.target.value = null;
      return;
    }
    form.setFieldsValue({
      attachedImages: [...(form.getFieldValue('attachedImages') || []), ...files],
    });
    e.target.value = null;
  };

  const handleRemoveFile = (index) => {
    const attachedFiles = form.getFieldValue('attachedFiles') || [];
    attachedFiles.splice(index, 1);
    form.setFieldsValue({ attachedFiles });
  };

  const handleRemoveImage = (index) => {
    const attachedImages = form.getFieldValue('attachedImages') || [];
    attachedImages.splice(index, 1);
    form.setFieldsValue({ attachedImages });
  };

  const handleInsertCode = () => {
    const textarea = getCommentTextarea();
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = form.getFieldValue('comment') || '';
    const selectedText = currentValue.slice(start, end);

    let newValue, cursorPosition;
    if (selectedText) {
      newValue =
        currentValue.slice(0, start) + '```\n' + selectedText + '\n```' + currentValue.slice(end);
      cursorPosition = start + selectedText.length + 8;
    } else {
      newValue = currentValue.slice(0, start) + '```\n\n```' + currentValue.slice(end);
      cursorPosition = start + 4;
    }

    form.setFieldsValue({ comment: newValue });
    setTimeout(() => {
      textarea.selectionStart = cursorPosition;
      textarea.selectionEnd = cursorPosition;
      textarea.focus();
    }, 0);
  };

  const insertText = (options) => {
    const textarea = getCommentTextarea();
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    const selectedText = value.substring(start, end);

    let transformedText, newStart, newEnd;
    if (options.transform) {
      const result = options.transform(selectedText, start, end);
      transformedText = result.text;
      newStart = result.newStart;
      newEnd = result.newEnd;
    } else {
      const { before, after } = options;
      transformedText = before + selectedText + after;
      newStart = start + before.length;
      newEnd = newStart + selectedText.length;
    }

    const newValue = value.substring(0, start) + transformedText + value.substring(end);
    form.setFieldsValue({ comment: newValue });

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newStart, newEnd);
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const textarea = getCommentTextarea();
      if (!textarea) return;
      const value = form.getFieldValue('comment') || '';
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      const lineEnd = value.indexOf('\n', start);
      const currentLine = value.slice(lineStart, lineEnd === -1 ? value.length : lineEnd);

      const unorderedMatch = currentLine.match(/^-\s/);
      const orderedMatch = currentLine.match(/^(\d+)\.\s/);

      if (unorderedMatch || orderedMatch) {
        e.preventDefault();
        let newValue, newCursorPos;

        if (unorderedMatch) {
          newValue = `${value.slice(0, start)}\n- ${value.slice(end)}`;
          newCursorPos = start + 3;
        } else {
          const nextNumber = parseInt(orderedMatch[1]) + 1;
          newValue = `${value.slice(0, start)}\n${nextNumber}. ${value.slice(end)}`;
          newCursorPos = start + `${nextNumber}. `.length + 1;
        }

        form.setFieldsValue({ comment: newValue });
        setTimeout(() => {
          textarea.selectionStart = newCursorPos;
          textarea.selectionEnd = newCursorPos;
        }, 0);
      }
    }
  };

  const handleSubmitComment = async (values) => {
    const { comment, attachedFiles, attachedImages, attachedCodeSnippet } = values;
    let mediaImages = [];
    let mediaFiles = [];

    if (!hasCommentContent(values)) {
      message.destroy();
      message.error('Comment or an attachment is required.');
      return;
    }

    try {
      setButtonDisabled(true);
      message.loading('Posting comment...', 0);

      if (attachedImages && attachedImages.length) {
        mediaImages = await handleUploadFilesToS3(attachedImages);
      }
      if (attachedFiles && attachedFiles.length) {
        mediaFiles = await handleUploadFilesToS3(attachedFiles);
      }

      const commentData = {
        comment: comment || '',
        attachedImages: mediaImages.length ? { create: mediaImages } : {},
        attachedFiles: mediaFiles.length ? { create: mediaFiles } : {},
        codeSnippet: attachedCodeSnippet || '',
        createdBy: { connect: { id: currentUser.id } },
        task: { connect: { id: currentTaskId } },
      };

      const mentions = getMentionedUserIds(comment);

      await createComment({ variables: { data: commentData, mentions } });

      form.resetFields();
      setMentionSearch('');
      await refetchTask();

      if (onCommentPosted) onCommentPosted();

      message.destroy();
      message.success('Comment posted successfully!');
      setButtonDisabled(false);
      setInputExpanded(false);
      return true;
    } catch (error) {
      message.destroy();
      message.error(`Failed to post comment: ${error.message}`);
      setButtonDisabled(false);
      return false;
    }
  };

  // Commenting on a closed task asks whether to re-open it first. The context task is the one
  // this page loaded, so guard against a stale/other task still sitting in the cache.
  const {
    reopenPromptVisible,
    submitting,
    currentStatus,
    reopenStatus,
    submitWithReopenCheck,
    confirmReopen,
    cancelReopenPrompt,
  } = useReopenOnComment({
    currentTask: currentTask && currentTask.id === currentTaskId ? currentTask : null,
    onReopened: onCommentPosted,
  });

  // An empty comment must fail validation BEFORE the re-open prompt appears — the emptiness
  // check lives inside handleSubmitComment, which the prompt would otherwise defer.
  const onFinishComment = (values) =>
    hasCommentContent(values)
      ? submitWithReopenCheck(() => handleSubmitComment(values))
      : handleSubmitComment(values);

  const onButtonClick = () => form.submit();

  if (!currentTaskId) return null;

  return (
    <Form
      form={form}
      onFinish={onFinishComment}
      layout="vertical"
    >
      <div
        className={`task-floating-input${
          inputExpanded
            ? ' task-floating-input--expanded'
            : ' task-floating-input--collapsed'
        }`}
      >
        {!inputExpanded && (
          <button type="button" className="task-comment__input-pill" onClick={expandInput}>
            <MessageOutlined className="task-comment__input-pill-icon" />
            <span className="task-comment__input-pill-text">Write a comment…</span>
          </button>
        )}
        {inputExpanded && (
          <>
            <Row className="task-comment__input-container">
              <FileDropPasteArea
                onImagesAdded={(newImages) => {
                  const currentImages = form.getFieldValue('attachedImages') || [];
                  form.setFieldsValue({ attachedImages: [...currentImages, ...newImages] });
                }}
                onFilesAdded={(newFiles) => {
                  const currentFiles = form.getFieldValue('attachedFiles') || [];
                  form.setFieldsValue({ attachedFiles: [...currentFiles, ...newFiles] });
                }}
              >
                <div className="task-comment__toolbar">
                  <Tooltip title="Bold" mouseEnterDelay={0.5}>
                    <Button
                      icon={<BoldOutlined />}
                      onClick={() => insertText({ before: '**', after: '**' })}
                      type="text"
                    />
                  </Tooltip>
                  <Tooltip title="Italic" mouseEnterDelay={0.5}>
                    <Button
                      icon={<ItalicOutlined />}
                      onClick={() => insertText({ before: '*', after: '*' })}
                      type="text"
                    />
                  </Tooltip>
                  <Tooltip title="Strikethrough" mouseEnterDelay={0.5}>
                    <Button
                      icon={<StrikethroughOutlined />}
                      onClick={() => insertText({ before: '~~', after: '~~' })}
                      type="text"
                    />
                  </Tooltip>
                  <Tooltip title="Ordered list" mouseEnterDelay={0.5}>
                    <Button
                      icon={<OrderedListOutlined />}
                      onClick={() =>
                        insertText({
                          transform: (selectedText, start) => {
                            if (selectedText === '') {
                              return { text: '1. ', newStart: start + 3, newEnd: start + 3 };
                            }
                            const lines = selectedText
                              .split('\n')
                              .filter((line) => line.trim() !== '');
                            const numbered = lines.map((line, i) => `${i + 1}. ${line}`).join('\n');
                            return { text: numbered, newStart: start, newEnd: start + numbered.length };
                          },
                        })
                      }
                      type="text"
                    />
                  </Tooltip>
                  <Tooltip title="Bulleted list" mouseEnterDelay={0.5}>
                    <Button
                      icon={<UnorderedListOutlined />}
                      onClick={() =>
                        insertText({
                          transform: (selectedText, start) => {
                            if (selectedText === '') {
                              return { text: '- ', newStart: start + 2, newEnd: start + 2 };
                            }
                            const lines = selectedText
                              .split('\n')
                              .filter((line) => line.trim() !== '');
                            const dashed = lines.map((line) => `- ${line}`).join('\n');
                            return { text: dashed, newStart: start, newEnd: start + dashed.length };
                          },
                        })
                      }
                      type="text"
                    />
                  </Tooltip>
                </div>
                <Form.Item noStyle shouldUpdate>
                  {(formInstance) => {
                    const attachedFiles = formInstance.getFieldValue('attachedFiles') || [];
                    const attachedImages = formInstance.getFieldValue('attachedImages') || [];

                    return attachedFiles.length > 0 || attachedImages.length > 0 ? (
                      <>
                        <div className="task-comment__attachment-preview">
                          {attachedFiles.length > 0 && (
                            <Row className="task-comment__attachment-row">
                              {attachedFiles.map((file, index) => (
                                <Tag
                                  closable
                                  key={index}
                                  onClose={() => handleRemoveFile(index)}
                                  icon={<PaperClipOutlined />}
                                  className="task-comment__attachment-tag"
                                >
                                  {file.name.length > 30
                                    ? `${file.name.slice(0, 30)}...`
                                    : file.name}
                                </Tag>
                              ))}
                            </Row>
                          )}
                          {attachedImages.length > 0 && (
                            <Row className="task-comment__attachment-row">
                              {attachedImages.map((file, index) => (
                                <Tag
                                  closable
                                  key={index}
                                  onClose={() => handleRemoveImage(index)}
                                  icon={<PictureOutlined />}
                                  className="task-comment__attachment-tag"
                                >
                                  {file.name.length > 30
                                    ? `${file.name.slice(0, 30)}...`
                                    : file.name}
                                </Tag>
                              ))}
                            </Row>
                          )}
                        </div>
                        <Row style={{ flexGrow: 1 }}>
                          <Form.Item name="comment" noStyle>
                            <Mentions
                              ref={textareaRef}
                              onKeyDown={handleKeyDown}
                              onChange={(text) => {
                                const next = text.slice(0, 3000);
                                form.setFieldsValue({ comment: next });
                                formInstance.setFieldsValue({ comment: next });
                              }}
                              onSearch={(text) => setMentionSearch(text)}
                              filterOption={false}
                              autoSize={{ minRows: 7, maxRows: 12 }}
                              maxLength={3000}
                              placeholder="Enter task details"
                              className="task-comment__textarea task-comment__textarea--with-attachments"
                            >
                              {renderMentionOptions()}
                            </Mentions>
                          </Form.Item>
                        </Row>
                      </>
                    ) : (
                      <Row style={{ flexGrow: 1 }}>
                        <Form.Item name="comment" noStyle>
                          <Mentions
                            className="custom-textarea task-comment__textarea"
                            onKeyDown={handleKeyDown}
                            ref={textareaRef}
                            onChange={(text) => {
                              const next = text.slice(0, 3000);
                              form.setFieldsValue({ comment: next });
                              formInstance.setFieldsValue({ comment: next });
                            }}
                            onSearch={(text) => setMentionSearch(text)}
                            filterOption={false}
                            maxLength={3000}
                            autoSize={{ minRows: 7, maxRows: 12 }}
                            placeholder="Add a comment"
                          >
                            {renderMentionOptions()}
                          </Mentions>
                        </Form.Item>
                      </Row>
                    );
                  }}
                </Form.Item>

                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  multiple
                  accept={allowedFileExtensions.join(',')}
                  onChange={handleFileChange}
                />
                <input
                  type="file"
                  ref={imageInputRef}
                  style={{ display: 'none' }}
                  multiple
                  accept={allowedImageExtensions.join(',')}
                  onChange={handleImageChange}
                />
              </FileDropPasteArea>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.isCodeSnippet !== currentValues.isCodeSnippet ||
                  prevValues.commentsCodeValue !== currentValues.commentsCodeValue
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue('isCodeSnippet') &&
                  getFieldValue('commentsCodeValue') && (
                    <Col className="task-comment__code-snippet">
                      <CopyBlock
                        text={getFieldValue('commentsCodeValue')}
                        language="javascript"
                        wrapLongLines
                        showLineNumbers={false}
                        theme="dracula"
                        codeBlock
                      />
                    </Col>
                  )
                }
              </Form.Item>
            </Row>

            <Row className="task-comment__actions-row">
              <Col className="task-comment__upload-buttons">
                <Form.Item name="attachedFiles" noStyle>
                  <Tooltip title="Upload file" mouseEnterDelay={0.3}>
                    <Button className="task-comment__upload-btn" onClick={handleUploadFileClick}>
                      <PaperClipOutlined />
                    </Button>
                  </Tooltip>
                </Form.Item>
                <Form.Item name="attachedImages" noStyle>
                  <Tooltip title="Upload image" mouseEnterDelay={0.3}>
                    <Button className="task-comment__upload-btn" onClick={handleUploadImageClick}>
                      <PictureOutlined />
                    </Button>
                  </Tooltip>
                </Form.Item>
                <Tooltip title="Code block" mouseEnterDelay={0.3}>
                  <Button className="task-comment__upload-btn" onClick={handleInsertCode}>
                    <CodeOutlined />
                  </Button>
                </Tooltip>
              </Col>
              <Col className="task-comment__submit-area">
                <Button
                  onClick={collapseInput}
                  disabled={buttonDisabled}
                  className="task-comment__cancel"
                  icon={<MinusOutlined />}
                >
                  Minimize
                </Button>
                <Button
                  onClick={onButtonClick}
                  disabled={buttonDisabled}
                  className="task-comment__submit"
                >
                  {buttonDisabled && <LoadingOutlined />} Submit
                </Button>
              </Col>
            </Row>
          </>
        )}
      </div>

      <TaskReopenPromptModal
        visible={reopenPromptVisible}
        currentStatus={currentStatus}
        reopenStatus={reopenStatus}
        loading={submitting}
        onReopen={confirmReopen}
        onCancel={cancelReopenPrompt}
      />
    </Form>
  );
};

TaskFloatingCommentInput.propTypes = {
  currentTaskId: PropTypes.string,
  onCommentPosted: PropTypes.func,
  formRef: PropTypes.object,
};

TaskFloatingCommentInput.defaultProps = {
  currentTaskId: null,
  onCommentPosted: null,
  formRef: null,
};

// Memoize so parent state churn (assignee/details/status changes) doesn't
// re-render the editor while the user is typing — the draft is held inside
// the antd Form instance, but minimizing re-renders also keeps cursor/IME
// state stable.
export default memo(TaskFloatingCommentInput);
